import * as React from 'react';
import DateTime, { DatetimepickerProps, TimeConstraints } from 'react-datetime';
import onClickOutside from 'react-onclickoutside';
import moment from 'moment';

// Removed getTimezone import as displayTimeZone is no longer used
// import { getTimezone } from '@utils/CalendarUtil';

import './style.scss';

const TypedDateTime = DateTime as React.ComponentType<
  DatetimepickerProps & {
    renderInput?: (props: any, openCalendar: () => void, closeCalendar: () => void) => void;
  }
>;

interface IDatePickerProps {
  name: string;
  value: any;
  onChange: TButtonOnClick;
  onBlur: TButtonOnClick;
  onFocus?: TButtonOnClick;
  dateFormat?: boolean | string;
  timeFormat?: boolean | string;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  inputId?: string;
  ariaLabel?: string;
  constraints?: {
    min?: moment.Moment;
    max?: moment.Moment;
  };
  timeConstraints?: TimeConstraints;
  enableTimeZone?: boolean;
  formatAsDateOnly?: boolean; // CDS-6255: When true, format as YYYY-MM-DD without UTC conversion
}

interface IDatePickerState {
  isOpen: boolean;
}

class DateTimePicker extends React.Component<IDatePickerProps, IDatePickerState> {
  static defaultProps = {
    name: '',
    value: null,
    placeholder: '',
    disabled: false,
    required: false,
    timeFormat: false,
    dateFormat: 'MM/DD/YYYY',
    constraints: null,
    enableTimeZone: true,
    onFocus: () => null,
    timeConstraints: {},
  };

  constructor(props: IDatePickerProps) {
    super(props);

    this.state = { isOpen: false };
  }

  onChange = (newValue: any) => {
    // Handle the case where react-datetime might pass a string or moment object
    let momentValue;
    if (typeof newValue === 'string') {
      momentValue = moment(newValue);
    } else if (moment.isMoment(newValue)) {
      momentValue = newValue;
    } else if (newValue instanceof Date) {
      momentValue = moment(newValue);
    } else {
      momentValue = null;
    }

    // Ensure the moment value is valid
    momentValue = momentValue && momentValue.isValid() ? momentValue : null;
    
    // When formatAsDateOnly, extract the local calendar date and anchor to midday UTC
    // to prevent timezone-induced date shifts.
    const formattedValue = momentValue 
      ? (this.props.formatAsDateOnly 
          ? moment.utc(momentValue.format('YYYY-MM-DD'), 'YYYY-MM-DD').add(12, 'hours').format('YYYY-MM-DDTHH:mm:ss.SSS[Z]')
          : momentValue.clone().utc().format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'))
      : null;
    const { constraints, value } = this.props;

    if (!constraints) {
      this.props.onChange({
        currentTarget: {
          name: this.props.name,
          value: formattedValue,
        },
        target: {
          name: this.props.name,
          value: formattedValue,
        },
      });
    } else {
      const { min, max } = constraints;
      let constrainedValue = momentValue;
      if (min && momentValue) {
        constrainedValue = min.isAfter(momentValue) ? moment(value) : momentValue;
      }
      if (max && momentValue) {
        constrainedValue = momentValue.isAfter(max) ? moment(value) : momentValue;
      }

      const formattedConstrainedValue = constrainedValue && constrainedValue.isValid() 
        ? (this.props.formatAsDateOnly
            ? moment.utc(constrainedValue.format('YYYY-MM-DD'), 'YYYY-MM-DD').add(12, 'hours').format('YYYY-MM-DDTHH:mm:ss.SSS[Z]')
            : constrainedValue.clone().utc().format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'))
        : null;

      this.props.onChange({
        currentTarget: {
          name: this.props.name,
          value: formattedConstrainedValue,
        },
        target: {
          name: this.props.name,
          value: formattedValue,
        },
      });
    }

    // To be in consistent with closeOnSelect = true
    this.setState({
      isOpen: false,
    });
  };

  handleClickOutside = (e: React.SyntheticEvent<MouseEvent>) => {
    this.setState((prevState) => ({
      isOpen: false,
    }));
  };

  toggleCalendar = (openCalendar, closeCalendar) => (event: any) => {
    if (!this.props.disabled) {
      this.props.onFocus(event);
      this.setState(
        (prevState) => ({
          ...prevState,
          isOpen: !prevState.isOpen,
        }),
        () => {
          if (this.state.isOpen) {
            openCalendar();
          } else {
            closeCalendar();
          }
        }
      );
    }
  };

  renderInput = (props, openCalendar, closeCalendar) => {
    const { name, value, placeholder, disabled, id } = props;
    const ariaLabel = props['aria-label'];
    const onFocus = (e: React.SyntheticEvent<HTMLInputElement>) => {
      openCalendar(e);

      this.props.onFocus(e);
    };

    return [
      <input
        key={1}
        id={id}
        name={name}
        placeholder={placeholder}
        disabled={disabled}
        type="text"
        value={value}
        onClick={this.toggleCalendar(openCalendar, closeCalendar)}
        onFocus={onFocus}
        onChange={this.onChange}
        readOnly={true}
        aria-label={ariaLabel}
      />,
      <span key={2} onClick={this.toggleCalendar(openCalendar, closeCalendar)} className="datepicker-image" />,
    ];
  };

  render() {
    const {
      name,
      value,
      placeholder,
      disabled,
      required,
      timeFormat,
      dateFormat,
      constraints,
      timeConstraints,
      inputId,
      ariaLabel,
    } = this.props;

    const isValidDate = (current: moment.Moment) => (constraints ? current.isAfter(constraints.min) : true);

    return (
      <TypedDateTime
        inputProps={{
          name,
          placeholder,
          disabled,
          required,
          ...(inputId ? { id: inputId } : {}),
          ...(ariaLabel ? { 'aria-label': ariaLabel } : {}),
          ...(required ? { 'aria-required': 'true' } : { 'aria-required': 'false' }),
        }}
        renderInput={this.renderInput}
        dateFormat={dateFormat}
        timeFormat={timeFormat}
        value={value}
        onChange={this.onChange}
        // Removed displayTimeZone to prevent timezone conversion issues
        // displayTimeZone={enableTimeZone ? getTimezone() : null}
        utc={false}
        isValidDate={isValidDate}
        closeOnSelect={true}
        timeConstraints={timeConstraints}
      />
    );
  }
}

export default onClickOutside(DateTimePicker);
